// landing/HeroSection.jsx
import React, { useState } from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';

/* ========================
 * handleScrollTo - Helper function for smooth scrolling
 * ========================*/
const handleScrollTo = (e, id) => {
  e.preventDefault();
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};

/* ========================
 * ChatUIScreenshot - Displays Chat UI Screenshot with a smooth skeleton fade
 * ========================*/
const ChatUIScreenshot = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgUrl = '/frame_generic_dark.png';

  return (
    <div className="relative animate-float order-1 lg:order-2 mt-14 max-w-md mx-auto lg:max-w-none">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-500 opacity-10 blur-xl rounded-2xl" />

      {/* Skeleton sits behind the image and fades out once loaded */}
      <div className={`absolute inset-0 rounded-2xl border border-white/10 bg-gray-900/60 transition-opacity duration-500 ${isLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div className="h-full w-full rounded-2xl bg-gradient-to-r from-white/[0.02] via-white/[0.06] to-white/[0.02] animate-pulse" />
      </div>

      {hasError ? (
        <div className="rounded-2xl border border-white/10 bg-gray-900/60 p-8 flex flex-col items-center justify-center min-h-[280px] gap-2">
          <p className="text-gray-300 font-medium">Preview unavailable</p>
          <p className="text-sm text-gray-500">Sign up to experience it yourself</p>
        </div>
      ) : (
        <img
          src={imgUrl}
          alt="Shelbula Chat UI"
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
          className={`rounded-2xl shadow-2xl border border-white/10 image-true-color w-full transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
};

/* ========================
 * HeroSection - Displays the main hero section of the landing page
 * Includes headline, call to action, and product preview
 * ========================*/
const HeroSection = ({ onSignupClick }) => {
  return (
    <section className="relative pt-36 pb-24 px-6 z-10">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left space-y-6 order-2 lg:order-1">
            <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full mx-auto lg:mx-0">
              <span className="text-orange-400 font-medium text-sm">OpenAI · Claude · Gemini · Mistral · Grok · OpenRouter</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold leading-tight">
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">Your Private AI Workspace</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              Use Claude, OpenAI, Gemini, and more through your own APIs, with encrypted memory, project context, and tools that carry across everywhere you work.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={onSignupClick}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 shadow-lg"
              >
                Start Free <ArrowRight className="w-5 h-5" />
              </button>
              
              <a 
                href="#compare"
                onClick={(e) => handleScrollTo(e, 'compare')}
                className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center"
              >
                See Pricing
              </a>
            </div>
            
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-center lg:justify-start text-sm text-gray-400">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-1.5" />
                3 local workspaces free
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-1.5" />
                Private by default
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-1.5" />
                Upgrade for secure cloud sync
              </div>
            </div>
          </div>
          
          <ChatUIScreenshot />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;